import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import { Box } from "@material-ui/core";
import { RouteComponentProps } from "react-router-dom";
import {
  HotelShopRoomTypePickerVariant,
  HotelShopRoomTypePickerEnum,
  RefundableRoomDetails,
  useDeviceTypes,
} from "halifax";
import queryStringParser from "query-string";

import {
  QuoteId,
  CallState,
  RoomProductType,
  RoomProductVariant,
  VIEWED_PC_DETAILS,
  SELECT_PC_ROOM,
  CfarQuoteResultEnum,
  RoomProductWithTransformedIndexes,
  LodgingCollectionEnum,
  VIEWED_LC_DETAILS,
  SELECT_LC_ROOM,
  StayTypesEnum,
} from "redmond";
import { PremierCollectionShopConnectorProps } from "./container";
import { DesktopShop } from "./components/DesktopShop";
import { MobileShop } from "./components/MobileShop";
import { CfarDetails } from "../ancillary/components";
import { PORTAL_TITLE, SELECT_ROOM_TITLE } from "./textConstants";
import { handlePageRedirectOnSelectRoomProduct } from "./utils/queryStringHelpers";
import { ClientContext } from "../../App";
import {
  AVAILABLE,
  ALLOWED_PAY_LATER_LODGINGS,
  getExperimentVariant,
  getExperimentVariantJSON,
  LC_GATING_EXPERIMENT,
  PREMIER_COLLECTION_EXPERIMENT,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  AllowedPayLaterLodgingsConfig,
  useExperiments,
} from "../../context/experiments";
import { PATH_HOME } from "../../utils/paths";
import { STAYS_WISHLIST } from "@capone/experiments";
import {
  useGetAllWishlistsQuery,
  useGetAllWishlistItemsByUserQuery,
  wishlistsApi,
  travelAppInterface,
  TravelAppInterfaceEventName,
  isCorpTenant,
  shouldCheckLifestyleEligibility,
} from "@capone/common";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { config } from "../../api/config";
import "./styles.scss";

import { useSelector } from "react-redux";

export interface IHotelShopProps
  extends PremierCollectionShopConnectorProps,
    RouteComponentProps {}

export const PremierCollectionShop = (props: IHotelShopProps) => {
  const {
    fetchPremierCollectionShop,
    setSelectedAccount,
    history,
    rewardsAccounts,
    viewedPremierCollectionDetailsProperties,
    trackingProps,
    selectRoomType,
    fetchTravelWalletDetails,
    fetchedPremierCollectionDetails,
    premierCollectionShopRequestId,
    isHotelCfarEnabled,
    fetchCfarQuotes,
    setSelectedCfarId,
    isAddOnOptionAvailable,
    hasSelectedRefundableRoom,
    setHasSelectedRefundableRoom,
    roomInfoProducts,
    roomInfoProductsWithTransformedIndexes,
    fetchTravelWalletCreditHistory,
    selectedLodging,
    setStayType,
    listPaymentMethods,
    setSelectedLodgingIsFavorited,
    setAllowedPayLaterLodgingIds,
    isTravelApp,
    checkLifestyleEligibility,
    setLifestyleEligibility,
    isLifestyleEligible,
  } = props;
  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const [isReadyToRedirect, setIsReadyToRedirect] = useState<boolean>(false);
  const [openRefundableRoomDetails, setOpenRefundableRoomDetails] =
    useState<boolean>(false);
  const [refundableRoomProduct, setRefundableRoomProduct] =
    useState<RoomProductWithTransformedIndexes>();

  const expState = useExperiments();

  const allowedPayLaterLodgingsConfig = getExperimentVariantJSON(
    expState.experiments,
    ALLOWED_PAY_LATER_LODGINGS
  ) as AllowedPayLaterLodgingsConfig | null;

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = useMemo(() => {
    return travelCreditHistoryExperiment === AVAILABLE;
  }, [travelCreditHistoryExperiment]);

  const isLifestyleCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  useEffect(() => {
    setAllowedPayLaterLodgingIds(allowedPayLaterLodgingsConfig?.data?.lodgingIds ?? []);
  }, [setAllowedPayLaterLodgingIds, allowedPayLaterLodgingsConfig]);

  // Initialize wishlist queries when wishlist experiment is enabled
  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const queryString = useMemo(
    () => queryStringParser.parse(history.location.search),
    [history.location.search]
  );

  const hasPageLoadedEventFired = useRef(false);

  const handleReadyToRedirect = () => {
    // note: it needs to wait for `selectRoomType` to be executed before proceeding with the page redirect
    setTimeout(() => {
      setIsReadyToRedirect(true);
    });
  };

  const roomInfoProductsType: HotelShopRoomTypePickerVariant = {
    roomInfoProductsWithTransformedIndexes,
    customComponents: {
      RefundableRoomDetails: (props: {
        roomProduct: RoomProductWithTransformedIndexes;
      }) => (
        <RefundableRoomDetails
          {...props}
          setRefundableRoomProduct={setRefundableRoomProduct}
          setOpenRefundableRoomDetails={setOpenRefundableRoomDetails}
          isMobile={matchesMobile}
        />
      ),
    },
    customStates: {
      // note: refundable room is disabled on agent portal
      isRefundableRoomDisabled: isAgentPortal,
      hasSelectedRefundableRoom,
      setHasSelectedRefundableRoom: (
        hasSelectedRefundableRoom: boolean,
        quoteId?: QuoteId
      ) => {
        setHasSelectedRefundableRoom(hasSelectedRefundableRoom);
        setSelectedCfarId(
          hasSelectedRefundableRoom && !!quoteId ? quoteId : null
        );
      },
    },
    variant: HotelShopRoomTypePickerEnum.WithTransformedIndexes,
  };

  useEffect(() => {
    if (selectedLodging) {
      document.title = selectedLodging.lodging.name;
    }
  }, [selectedLodging]);

  useEffect(() => {
    fetchTravelWalletDetails();
    listPaymentMethods();
    setStayType(StayTypesEnum.Hotels);
    document.title = SELECT_ROOM_TITLE;
    setTimeout(() => window.scrollTo(0, 0), 0);

    return () => {
      document.title = PORTAL_TITLE;
    };
  }, []);

  // Check eligibility once rewards accounts have loaded
  useEffect(() => {
    // Wait for rewards accounts to load before checking eligibility
    if (rewardsAccounts.length === 0) {
      return;
    }

    // Only check eligibility for corporate tenant with eligible cards
    const isCorporate = isCorpTenant(config.TENANT);
    if (shouldCheckLifestyleEligibility(isCorporate, rewardsAccounts)) {
      checkLifestyleEligibility();
    } else {
      // Set to false immediately if not checking
      setLifestyleEligibility(false);
    }
  }, [rewardsAccounts, checkLifestyleEligibility, setLifestyleEligibility]);

  useEffect(() => {
    if (fetchedPremierCollectionDetails) {
        const vibesProperties = {
          source: queryString.source,
          vibe_name: queryString.vibe ?? "",
        };

      trackEvent({
        eventName:
          selectedLodging?.lodgingCollection === LodgingCollectionEnum.Lifestyle
            ? VIEWED_LC_DETAILS
            : VIEWED_PC_DETAILS,
        properties: {
          ...trackingProps?.properties,
        ...viewedPremierCollectionDetailsProperties.properties,
        ...vibesProperties
        },
        encryptedProperties: [
          ...trackingProps?.encryptedProperties ?? [],
          ...viewedPremierCollectionDetailsProperties.encryptedProperties ?? [],
        ],
        
      });

      if (!hasPageLoadedEventFired.current) {
        travelAppInterface.sendEvent({
          name: TravelAppInterfaceEventName.PAGE_LOADED,
        });
        hasPageLoadedEventFired.current = true;
      }
    }
  }, [fetchedPremierCollectionDetails]);

  useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(expState.callState) ||
      expState.experiments.length > 0
    ) {
      // Check if user is eligible via experiments or API
      const isEligibleViaExperiments = isLifestyleCollectionEnabled || isPremierCollectionEnabled;
      const isEligibleViaAPI = isLifestyleEligible === true;

      // If eligibility is still loading (null) for lifestyle lodgings, wait for it
      const isEligibilityStillLoading = isLifestyleEligible === null && !isEligibleViaExperiments;

      if (!isEligibilityStillLoading) {
        // If it's a lifestyle lodging, check eligibility
        if (!isEligibleViaExperiments && !isEligibleViaAPI) {
          // User is not eligible for lifestyle collection - redirect to home
          history.push(PATH_HOME);
        } else {
          // User is eligible or it's not a lifestyle lodging - fetch shop
          fetchPremierCollectionShop(history, {
            overrideStateByQueryParams: true,
          });
          setTimeout(() => window.scrollTo(0, 0), 0);
        }
      }
    }
  }, [expState.callState, queryString.lodgingId, isLifestyleEligible]);

  useEffect(() => {
    if (isHotelCfarEnabled && premierCollectionShopRequestId) {
      fetchCfarQuotes({ opaqueRequest: premierCollectionShopRequestId });
    }
  }, [premierCollectionShopRequestId]);

  useEffect(() => {
    const { selectedAccountIndex } = queryStringParser.parse(
      history.location.search
    );

    if (selectedAccountIndex && rewardsAccounts.length > 0) {
      setSelectedAccount(
        rewardsAccounts[Number(selectedAccountIndex)]?.accountReferenceId
      );
    }
  }, [rewardsAccounts]);

  /*
    note: selectRoomType (the action that updates indexes in redux state) is called within a halifax component as a callback;
    it's executed at the same time as when `onClickContinue` is handled, which means that some states are not yet updated.
  */
  useEffect(() => {
    if (isReadyToRedirect) {
      handlePageRedirectOnSelectRoomProduct({
        history,
        isAddOnOptionAvailable,
        trackBookStep: !!isTravelApp,
      });
      setIsReadyToRedirect(false);
    }
  }, [history, isAddOnOptionAvailable, isReadyToRedirect]);

  useEffect(() => {
    if (isTravelCreditHistoryExperiment) {
      fetchTravelWalletCreditHistory();
    }
  }, [isTravelCreditHistoryExperiment]);

  const {
    data: favoritedLodgings,
    isSuccess: isFavoritedLodgingsSuccess,
    isError: isFavoritedLodgingsError,
  } = useSelector(wishlistsApi.endpoints.getAllWishlistItemsByUser.select());

  const isWishlistsLoaded =
    isFavoritedLodgingsSuccess || isFavoritedLodgingsError;

  const isFavorited = favoritedLodgings?.has(selectedLodging?.lodging.id || "");

  useEffect(() => {
    if (
      isWishlistExperiment &&
      isWishlistsLoaded &&
      isFavorited !== undefined
    ) {
      setSelectedLodgingIsFavorited(isFavorited);
    }
  }, [
    isWishlistExperiment,
    isWishlistsLoaded,
    isFavorited,
    setSelectedLodgingIsFavorited,
  ]);

  return (
    <>
      <Box className="premier-collection-shop-root">
        {matchesDesktop && (
          <DesktopShop
            roomInfoProductsType={roomInfoProductsType}
            handleReadyToRedirect={handleReadyToRedirect}
            variant={
              (isLifestyleCollectionEnabled || isPremierCollectionEnabled || isLifestyleEligible === true) &&
              selectedLodging?.lodgingCollection ===
                LodgingCollectionEnum.Lifestyle
                ? "lifestyle-collection"
                : "default"
            }
          />
        )}
        {matchesMobile && (
          <MobileShop
            roomInfoProductsType={roomInfoProductsType}
            handleReadyToRedirect={handleReadyToRedirect}
            variant={
              (isLifestyleCollectionEnabled || isPremierCollectionEnabled || isLifestyleEligible === true) &&
              selectedLodging?.lodgingCollection ===
                LodgingCollectionEnum.Lifestyle
                ? "lifestyle-collection"
                : "default"
            }
          />
        )}
      </Box>
      {!!refundableRoomProduct?.roomProductVariant &&
        refundableRoomProduct.roomProductVariant.variant ===
          RoomProductType.RefundableRoom && (
          <CfarDetails
            openCfarDetails={openRefundableRoomDetails}
            onClose={() => setOpenRefundableRoomDetails(false)}
            onContinue={() => {
              // note: refundable room is disabled on agent portal
              if (isAgentPortal) {
                return;
              }

              const quoteId = getQuoteId(
                refundableRoomProduct.roomProductVariant
              );
              const baseProduct =
                refundableRoomProduct.roomProductVariant?.baseProduct;

              if (!!quoteId) {
                setHasSelectedRefundableRoom(true);
                setSelectedCfarId(quoteId);
              }

              if (!!baseProduct) {
                selectRoomType(baseProduct.roomIndex, baseProduct.productIndex);
                trackEvent({
                  eventName:
                    selectedLodging?.lodgingCollection ===
                    LodgingCollectionEnum.Lifestyle
                      ? SELECT_LC_ROOM
                      : SELECT_PC_ROOM,
                  properties: {
                    room_type:
                      roomInfoProducts[baseProduct.roomIndex]?.roomInfo.name,
                  },
                });
                handleReadyToRedirect();
              }
            }}
            currentHotelCfarQuote={{
              id: refundableRoomProduct.roomProductVariant.quoteId,
              premiumPrices:
                refundableRoomProduct.roomProductVariant.premiumPrices,
              coverageAmount:
                refundableRoomProduct.roomProductVariant.coverageAmount,
              coveragePercentage:
                refundableRoomProduct.roomProductVariant.coveragePercentage,
              CfarQuoteResult: CfarQuoteResultEnum.HotelCfarQuote,
            }}
            currentCancellationPolicy={
              refundableRoomProduct.roomProductVariant.cancellationPolicy
            }
            cardContentProps={{ hideRadioButtons: true }}
            contentOnly={false}
            modalType="refundable-room"
            contentClassName="pc-refundable-room-details-modal"
          />
        )}
    </>
  );
};

const getQuoteId = (roomProductVariant: RoomProductVariant | undefined) =>
  roomProductVariant?.variant === RoomProductType.RefundableRoom
    ? roomProductVariant.quoteId
    : undefined;
